import { localized, t } from '@weavedev/lit-i18next';
import { css, html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, ref, Ref } from 'lit/directives/ref.js';
import { styleMap } from 'lit/directives/style-map.js';

import {
  deleteRemoveDebtorToken,
  getShowDebtorTokens,
  postCreateDebtorToken,
  TokenDto,
} from '@/api-sdk';
import { FgCreateTokenDialog } from '@/components/debtor-tokens/fg-create-token-dialog';
import { FgRemoveTokenDialog } from '@/components/debtor-tokens/fg-remove-token-dialog';
import {
  iconCopy,
  iconEye,
  iconEyeSlash,
  iconTrashCan,
} from '@/components/icons';
import {
  baseStyles,
  buttonStyles,
  formStyles,
  typographyStyles,
} from '@/styles';
import { MessageTypes, RowData, SnackbarState } from '@/types';
import { i18nInit, setBaseUrl } from '@/utils';

import '../fg-dialog';
import '../loader/fg-loader';
import '../messages/fg-message-snack-bar';
import '../tables/fg-table';
import './fg-create-token-dialog';
import './fg-remove-token-dialog';

@customElement('fg-debtor-api-access-tokens')
@localized()
export class FgDebtorApiAccessTokens extends LitElement {
  @property({ type: String })
  locale = '';
  @property({ type: String })
  debtor = '';
  @property({ type: String })
  baseUrl = '';

  @state()
  isLoading: boolean = true;

  @state()
  showSnackbarState = SnackbarState.NONE;

  @state()
  private tokens: (TokenDto & { visible?: boolean })[] = [];

  fgDialogCreateTokenRef: Ref<FgCreateTokenDialog> = createRef();
  fgDialogRemoveTokenRef: Ref<FgRemoveTokenDialog> = createRef();

  async firstUpdated() {
    await this.fetchTokens();
  }

  async scheduleUpdate() {
    setBaseUrl(this.baseUrl);

    window.i18next = window.i18next || i18nInit(this.locale);
    await window.i18next;

    super.scheduleUpdate();
  }

  async fetchTokens() {
    try {
      this.tokens = await getShowDebtorTokens({
        debtorCode: this.debtor,
      });
    } catch (error) {
      this.showSnackbarState = SnackbarState.ERROR;
      console.error(error);
    } finally {
      this.isLoading = false;
    }
  }

  async toggleTokenVisibility(index: number) {
    const token = this.tokens[index];
    token.visible = !token.visible;
    this.requestUpdate();
  }

  async copyToken(token: string) {
    try {
      await navigator.clipboard.writeText(token);
      this.showSnackbarState = SnackbarState.COPIED;
    } catch (error) {
      this.showSnackbarState = SnackbarState.ERROR;
      console.error(error);
    }
  }

  async deleteToken(tokenId: string) {
    try {
      await deleteRemoveDebtorToken({
        debtorCode: this.debtor,
        uuid: tokenId,
      });
      this.tokens = this.tokens.filter(token => token.uuid !== tokenId);
      this.fgDialogRemoveTokenRef?.value?.closeDialog();
      this.showSnackbarState = SnackbarState.DELETED;
    } catch (error) {
      this.showSnackbarState = SnackbarState.ERROR;
      console.error(error);
    }
  }

  async createToken(event: CustomEvent) {
    const token = event.detail.token;
    try {
      await postCreateDebtorToken({
        debtorCode: this.debtor,
        data: {
          name: token.name,
          environment: token.environment,
        },
      });
      await this.fetchTokens();
      this.fgDialogCreateTokenRef?.value?.closeDialog();
      this.showSnackbarState = SnackbarState.CREATED;
    } catch (error) {
      this.showSnackbarState = SnackbarState.ERROR;
      console.error(error);
    }
  }

  renderCreateTokenButton() {
    return html`
      <button
        @click="${() => this.fgDialogCreateTokenRef?.value?.openDialog()}"
        class="button button-primary"
      >
        ${t('actions.createToken')}
      </button>
    `;
  }

  renderCreateTokenDialog() {
    return html`
      <fg-create-token-dialog
        ${ref(this.fgDialogCreateTokenRef)}
        @create-token="${(event: CustomEvent) => this.createToken(event)}"
      ></fg-create-token-dialog>
    `;
  }

  getSnackbarState(
    showSnackbarState: SnackbarState,
  ): false | { type: MessageTypes; message: string } {
    switch (showSnackbarState) {
      case SnackbarState.COPIED:
        return {
          type: MessageTypes.SUCCESS,
          message: t('content.debtorToken.copiedToken') || '',
        };
      case SnackbarState.DELETED:
        return {
          type: MessageTypes.SUCCESS,
          message: t('content.debtorToken.deletedToken') || '',
        };
      case SnackbarState.ERROR:
        return {
          type: MessageTypes.ERROR,
          message: t('errors.somethingWentWrong') || '',
        };
      case SnackbarState.CREATED:
        return {
          type: MessageTypes.SUCCESS,
          message: t('content.debtorToken.createdToken') || '',
        };
      default:
        return false;
    }
  }

  render() {
    const snackbarState = this.getSnackbarState(this.showSnackbarState);

    if (this.isLoading) {
      return html`
        <div class="loading-container">
          <fg-loader .type="${'in-component'}"></fg-loader>
        </div>
      `;
    }

    if (this.tokens.length === 0) {
      return html`
        <div class="no-tokens-container">
          <span>${t('content.debtorToken.noTokens')}</span>
          ${this.renderCreateTokenButton()} ${this.renderCreateTokenDialog()}
        </div>
      `;
    }

    const tableHeaders = [
      t('content.debtorToken.tokenName'),
      t('content.debtorToken.environment'),
      t('content.debtorToken.tokenKey'),
      '',
    ];

    const rows: RowData = this.tokens.map((token, index) => ({
      rowName: token.name,
      rowValues: [
        { mainValue: token.environment },
        {
          mainValue: html`
            <div>
              <div style=${styleMap({ position: 'relative' })}>
                <input
                  type="${token.visible ? 'text' : 'password'}"
                  .value="${token.token}"
                  readonly
                  style=${styleMap({
                    paddingRight: '50px',
                  })}
                />
                <div
                  style=${styleMap({
                    position: 'absolute',
                    top: '10px',
                    right: '0',
                    marginLeft: '10px',
                    marginRight: '10px',
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '5px',
                  })}
                >
                  <button @click="${() => this.toggleTokenVisibility(index)}">
                    ${token.visible ? iconEyeSlash : iconEye}
                  </button>
                  <button @click="${() => this.copyToken(token.token)}">
                    ${iconCopy}
                  </button>
                </div>
              </div>
            </div>
          `,
        },
        {
          mainValue: html`
            <div class="actions">
              <button
                @click="${() =>
                  this.fgDialogRemoveTokenRef.value?.openDialog(token.uuid)}"
              >
                ${iconTrashCan}
              </button>
            </div>
          `,
        },
      ],
    }));

    return html`
      <div part="wholesale">
        <fg-table
          .columnHeadings="${tableHeaders}"
          .columnRows="${rows}"
        ></fg-table>
        ${this.renderCreateTokenButton()}
      </div>
      ${this.renderCreateTokenDialog()}
      <fg-remove-token-dialog
        ${ref(this.fgDialogRemoveTokenRef)}
        @delete-token="${(event: CustomEvent) =>
          this.deleteToken(event.detail.tokenId)}"
      ></fg-remove-token-dialog>
      <fg-message-snack-bar
        part="wholesale"
        autoDisappear
        ?show="${Boolean(this.showSnackbarState !== SnackbarState.NONE)}"
        .type="${snackbarState ? snackbarState.type : 'success'}"
        message="${snackbarState ? snackbarState.message : ''}"
        @on-close="${() => {
          this.showSnackbarState = SnackbarState.NONE;
        }}"
      ></fg-message-snack-bar>
    `;
  }

  static styles = [
    baseStyles,
    typographyStyles,
    formStyles,
    buttonStyles,
    css`
      .no-tokens-container {
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }

      .loading-container {
        position: relative;
        height: 200px;
      }
    `,
  ];
}
